"use client";

/**
 * PickCandidateStep — user picks one or many candidates for a hop.
 *
 * Single-select for non-terminal hops or single-target actions.
 * Multi-select (checkboxes) when terminal hop + allowMulti.
 */

import { useState } from "react";

import { Button } from "@west-tennessee-consulting/ui";

import type { CandidateRow } from "@/lib/actions/relationships/types";

import { useResolverContext } from "../ResolverContext";

interface Props {
  stepIndex: number;
  candidates: CandidateRow[];
  allowMulti: boolean;
  bulkMax: number;
}

export function PickCandidateStep({
  stepIndex,
  candidates,
  allowMulti,
  bulkMax,
}: Props) {
  const { setPick, setBulkPick } = useResolverContext();
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < bulkMax) next.add(id);
      return next;
    });
  };

  const submitSingle = (id: string) => setPick(stepIndex, id);

  const submitBulk = () => {
    if (selected.size === 0) return;
    setBulkPick(Array.from(selected));
  };

  if (!allowMulti) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground">Select one to continue</p>
        <ul className="divide-y rounded border">
          {candidates.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => submitSingle(c.id)}
                className="w-full text-left p-3 hover:bg-muted/50 focus:bg-muted/50"
              >
                <div className="font-medium">{c.label}</div>
                {c.subtitle && (
                  <div className="text-xs text-muted-foreground">
                    {c.subtitle}
                  </div>
                )}
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Select one or more recipients (up to {bulkMax}), then click Next.
      </p>
      <ul className="divide-y rounded border max-h-80 overflow-y-auto">
        {candidates.map((c) => {
          const checked = selected.has(c.id);
          return (
            <li key={c.id}>
              <label className="flex items-start gap-3 p-3 hover:bg-muted/50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(c.id)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="font-medium">{c.label}</div>
                  {c.subtitle && (
                    <div className="text-xs text-muted-foreground">
                      {c.subtitle}
                    </div>
                  )}
                </div>
              </label>
            </li>
          );
        })}
      </ul>
      <div className="flex justify-end gap-2">
        <Button
          onClick={submitBulk}
          disabled={selected.size === 0}
        >
          Next{selected.size > 0 ? ` (${selected.size})` : ""}
        </Button>
      </div>
    </div>
  );
}
